/**
 * Top-level React error boundary (design review 2026-07-15, P1-5).
 *
 * Without one, any uncaught render/lifecycle error unmounts the whole tree —
 * a white screen with no way back, potentially mid-payroll-run. This boundary
 * catches the error, shows a branded recovery screen, and offers a reload plus
 * a way back to the dashboard. Class component by necessity: error boundaries
 * have no hook equivalent (getDerivedStateFromError / componentDidCatch).
 *
 * Data safety note: by the time a render error throws, any server mutation has
 * already committed or failed — recovering the UI cannot double-run payroll.
 *
 * @module components/ErrorBoundary
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isStaleChunkError, reloadOnceForStaleChunk } from '../utils/staleChunkReload';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  /** A stale-chunk reload is in flight — show a quiet updating screen. */
  reloading: boolean;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // A failed page-chunk load after a deploy is not a crash — the tab is just
    // referencing the previous build's hashed files. Reload (once) to pick up
    // the new build; only fall through to the error card if that didn't help.
    if (isStaleChunkError(error) && reloadOnceForStaleChunk()) {
      this.setState({ reloading: true });
      return;
    }
    // Surface in the console for debugging / any future client-side error
    // reporting; the server-side Sentry seam does not see client render errors.
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  private readonly handleGoHome = (): void => {
    // Full navigation (not client-side routing) so the crashed tree is rebuilt.
    window.location.assign('/dashboard');
  };

  override render(): ReactNode {
    if (this.state.error === null) return this.props.children;

    if (this.state.reloading) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <p className="text-sm text-gray-600">Updating to the latest version…</p>
        </div>
      );
    }

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          <p className="mt-2 text-sm text-gray-600">
            The page hit an unexpected error. Your data is safe — anything already
            submitted was saved on the server. Reload to pick up where you left off.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={this.handleReload}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Reload page
            </button>
            <button
              onClick={this.handleGoHome}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Go to dashboard
            </button>
          </div>
          <p className="mt-4 break-all text-xs text-gray-500">{this.state.error.message}</p>
        </div>
      </div>
    );
  }
}
